import { useCallback } from 'react';
import { useFetchOld } from 'hooks';
import { ThrowableFetchError } from 'hooks/fetch';
import { getUser } from 'actions';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { LoadingAppViewModal } from 'design-system/LoadingAppView';
import Modal from 'utils/packages/Modal';
import Amplitude from 'utils/amplitude';
import Config from 'utils/packages/configs';
import { getBlackbox } from 'utils/packages/deviceRisk';
import Sentry from 'utils/sentry';
import { STAGING_BLACKBOX } from '../utils';
import CreditScoreOutsideUkModal from '../components/CreditScoreOutsideUkModal';
import { clearCreditScoreRegistrationCooldown, getCreditScore, requestCreditScoreFetchFlow, setCreditScoreRegistrationCooldown, startCreditScoreRegistration, } from '../actions';
import { selectCreditScoreCooldown } from '../selectors';
const strings = {
    success: {
        title: 'Success',
        subTitle: 'You can now access your credit score. Let’s check it!',
    },
    errorKyc: {
        title: 'We couldn’t find your report',
        subTitle: [
            'If you are using a VPN, please turn it off and try again.',
            'Still having trouble? This usually means you don’t have enough credit history. Don’t worry, we can help you start building your credit!',
        ],
    },
    errorAlreadyRegistered: {
        title: 'Report already active',
        subTitle: 'This credit score is already being monitored on another Emma account. This can happen if you have more than one account. If this wasn’t you, please contact support.',
    },
    error: {
        title: 'No match found',
        subTitle: [
            'We couldn’t find a credit score with those details. Please check your info is accurate and make sure your VPN is turned off.',
            'If you’ve recently moved to the UK, you might not have enough credit history yet. We can help you build one!',
        ],
        subTitleAlt: 'We couldn’t find a credit score with those details. If you’ve recently moved to the UK, you might not have enough credit history yet. We can help you build one!',
    },
    buttons: {
        close: 'Close',
        continue: 'Continue',
        buildCredit: 'See how to build credit',
        checkDetails: 'Check details and try again',
        tryAgain: 'Try again',
        contactSupport: 'Contact support',
    },
    loading: 'Finding your credit report...',
    // Same fallback copy the global ErrorModal uses.
    genericError: 'This is awkward, please try again.',
};
const isNonUkClientIpError = (error) => error.fetchError?.apiPayload?.fullResponse?.error?.name ===
    'NonUkClientIpError';
const useCreditScoreRegistration = (kycFlow) => {
    const [, , , fetch] = useFetchOld({ throws: true });
    const navigation = useMainStackNavigation();
    const dispatch = useAppDispatch();
    const cooldown = useAppSelector(selectCreditScoreCooldown);
    const onPressBuildCredit = useCallback(() => {
        navigation.navigate('CreditScoreBuildCredit');
    }, [navigation]);
    const onPressSupport = useCallback(() => {
        navigation.navigate('Support');
    }, [navigation]);
    const onPressClose = useCallback(() => {
        navigation.popToTop();
    }, [navigation]);
    const onPressCheckDetails = useCallback(() => {
        navigation.pop();
    }, [navigation]);
    const onSuccess = useCallback(() => {
        navigation.navigate('Success', {
            title: strings.success.title,
            subTitle: strings.success.subTitle,
            buttonText: strings.buttons.continue,
            onContinue: () => {
                onPressClose();
                return true;
            },
        });
    }, [onPressClose]);
    const onError = useCallback((params) => {
        navigation.navigate('CreditScoreError', params);
    }, []);
    const handleStatusResponse = useCallback(async (status, onRetry) => {
        if (status === 'REGISTERED') {
            dispatch(getUser());
            // Await /score before kicking off the fetch flow: the backend caches
            // the report it computes for /score, so /factors, /history and
            // /report dispatched by the flow return from the warm cache.
            try {
                await fetch(getCreditScore());
            }
            catch (err) {
                Sentry.logError({
                    filename: 'useCreditScoreRegistration.ts',
                    message: 'Post-registration credit score fetch failed',
                    err,
                });
            }
            dispatch(requestCreditScoreFetchFlow({
                includeReport: true,
                includeHistory: true,
                skipScore: true,
            }));
            onSuccess();
            return;
        }
        if (status === 'ALREADY_REGISTERED') {
            onError({
                errorTitle: strings.errorAlreadyRegistered.title,
                errorMessage: strings.errorAlreadyRegistered.subTitle,
                primaryButtonText: strings.buttons.close,
                primaryButtonOnPress: onPressClose,
                secondaryButtonText: strings.buttons.contactSupport,
                secondaryButtonOnPress: onPressSupport,
                alertIcon: true,
            });
            return;
        }
        const errorParams = kycFlow
            ? {
                errorTitle: strings.errorKyc.title,
                errorMessage: strings.errorKyc.subTitle,
                primaryButtonText: strings.buttons.tryAgain,
                primaryButtonOnPress: onRetry,
                secondaryButtonText: strings.buttons.buildCredit,
                secondaryButtonOnPress: onPressBuildCredit,
                tertiaryButtonText: strings.buttons.close,
                tertiaryButtonOnPress: onPressClose,
            }
            : {
                errorTitle: strings.error.title,
                errorMessage: strings.error.subTitle,
                primaryButtonText: strings.buttons.checkDetails,
                primaryButtonOnPress: onPressCheckDetails,
                secondaryButtonText: strings.buttons.buildCredit,
                secondaryButtonOnPress: onPressBuildCredit,
                tertiaryButtonText: strings.buttons.close,
                tertiaryButtonOnPress: onPressClose,
            };
        onError(errorParams);
    }, [
        dispatch,
        fetch,
        kycFlow,
        onError,
        onPressCheckDetails,
        onPressBuildCredit,
        onPressClose,
        onSuccess,
        onPressSupport,
    ]);
    const handleError = useCallback((error) => {
        const statusCode = error.fetchError?.apiPayload?.status;
        const fullResponse = error.fetchError?.apiPayload?.fullResponse;
        // The action mutes the global error modal for 409 and 429, so this
        // hook owns all UI for both. A 409 that isn't the non-UK error (that
        // one is handled in register()) gets the same generic modal the
        // global handler shows for other errors (see common/ErrorModal.tsx).
        if (statusCode === 409) {
            Modal.showError(fullResponse?.error?.userMessage ?? strings.genericError);
            return;
        }
        if (statusCode !== 429)
            return;
        const retryAfterSeconds = Number(fullResponse?.error?.retryAfterSeconds || 0);
        const errorTitle = kycFlow ? strings.errorKyc.title : strings.error.title;
        const errorMessage = kycFlow
            ? strings.errorKyc.subTitle
            : strings.error.subTitleAlt;
        if (retryAfterSeconds > 0) {
            dispatch(setCreditScoreRegistrationCooldown({
                canTryAgainOn: Date.now() + retryAfterSeconds * 1000,
                errorTitle,
                errorMessage,
            }));
        }
        onError({
            errorTitle,
            errorMessage,
            primaryButtonText: strings.buttons.buildCredit,
            primaryButtonOnPress: onPressBuildCredit,
            secondaryButtonText: strings.buttons.close,
            secondaryButtonOnPress: onPressClose,
            retryAfterSeconds,
        });
    }, [dispatch, kycFlow, onPressBuildCredit, onPressClose, onError]);
    const canRegisterAgain = useCallback((onAllowed) => {
        if (!cooldown || Date.now() >= cooldown.canTryAgainOn) {
            if (cooldown)
                dispatch(clearCreditScoreRegistrationCooldown());
            onAllowed();
            return;
        }
        const retryAfterSeconds = Math.max(1, Math.ceil((cooldown.canTryAgainOn - Date.now()) / 1000));
        onError({
            errorTitle: cooldown.errorTitle ?? strings.error.title,
            errorMessage: cooldown.errorMessage ?? strings.error.subTitleAlt,
            primaryButtonText: strings.buttons.buildCredit,
            primaryButtonOnPress: onPressBuildCredit,
            secondaryButtonText: strings.buttons.close,
            secondaryButtonOnPress: onPressClose,
            retryAfterSeconds,
        });
    }, [cooldown, dispatch, onError, onPressBuildCredit, onPressClose]);
    // Surfaces of the credit-score loading modal — colocated with `register`
    // so callers (e.g. the registration screen running pre-register saves)
    // can show the same modal without duplicating the title string.
    const showLoading = useCallback(() => {
        LoadingAppViewModal.show({ title: strings.loading });
    }, []);
    const hideLoading = useCallback((onHidden) => {
        LoadingAppViewModal.hide(onHidden);
    }, []);
    const register = useCallback(async function runRegistration(currentAddressId, previousAddressId) {
        Amplitude.logEvent('Submitted Request', {
            type: 'credit_score_application',
        });
        showLoading();
        const retry = () => canRegisterAgain(() => runRegistration(currentAddressId, previousAddressId));
        try {
            const blackBoxKey = await getBlackbox();
            const result = await fetch(startCreditScoreRegistration({
                currentAddressId,
                previousAddressId,
                blackBox: Config?.IS_PROD === 'true'
                    ? blackBoxKey
                    : STAGING_BLACKBOX.blackBox,
            }));
            if (result) {
                // Awaited so the loading modal stays up through the score fetch.
                await handleStatusResponse(result.status, retry);
            }
        }
        catch (error) {
            if (!(error instanceof ThrowableFetchError))
                return;
            if (isNonUkClientIpError(error)) {
                hideLoading(() => CreditScoreOutsideUkModal.show({
                    onContinue: () => runRegistration(currentAddressId, previousAddressId),
                    // Back to the credit tab, like the error screens' Close.
                    onTryLater: onPressClose,
                }));
                return;
            }
            handleError(error);
        }
        finally {
            hideLoading();
        }
    }, [
        canRegisterAgain,
        fetch,
        handleStatusResponse,
        handleError,
        onPressClose,
        showLoading,
        hideLoading,
    ]);
    return {
        register,
        canRegisterAgain,
        showLoading,
        hideLoading,
    };
};
export default useCreditScoreRegistration;
